import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Link,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff, Email, Lock } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

const Login = () => {
  const navigate = useNavigate();
  const { login, verify2FA, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });
  const [twoFactorToken, setTwoFactorToken] = useState('');
  const [twoFactorRequired, setTwoFactorRequired] = useState(false);
  const [adminId, setAdminId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'rememberMe' ? checked : value,
    }));
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login({
      email: formData.email,
      password: formData.password,
    });

    if (result.success) {
      if (result.twoFactorRequired) {
        setTwoFactorRequired(true);
        setAdminId(result.adminId);
        setLoading(false);
      } else {
        navigate('/');
      }
    } else {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await verify2FA({
      adminId,
      token: twoFactorToken,
    });

    setLoading(false);

    if (result.success) {
      navigate('/');
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Left Side: Login Form - 65% width, centered */}
      <Box
        sx={{
          flex: '0 0 65%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#ffffff',
          padding: '40px 60px',
          '@media (max-width: 960px)': {
            flex: '0 0 100%',
            padding: '40px 24px',
          },
        }}
      >
        <Box sx={{ width: '100%', maxWidth: '400px' }}>
          <Box sx={{ mb: 5 }}>
            <Typography
              component="h1"
              variant="h4"
              sx={{
                fontWeight: 700,
                mb: 1,
                color: '#1e293b',
                fontSize: '1.75rem',
              }}
            >
              Admin Login
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.95rem' }}>
              {twoFactorRequired 
                ? 'Enter the 6-digit code from your authenticator app.' 
                : 'Welcome back! Please enter your details to continue.'}
            </Typography>
          </Box>

          {twoFactorRequired ? (
            <Box component="form" noValidate onSubmit={handleVerify2FA}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="twoFactorToken"
                label="2FA Code"
                name="twoFactorToken"
                autoFocus
                value={twoFactorToken}
                onChange={(e) => setTwoFactorToken(e.target.value)}
                disabled={loading}
                inputProps={{ maxLength: 6 }}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#ffffff',
                    borderRadius: '8px',
                    fontSize: '1.2rem',
                    letterSpacing: '0.5rem',
                    textAlign: 'center',
                    height: '56px',
                    '& fieldset': {
                      borderColor: '#e2e8f0',
                      borderWidth: '1.5px',
                    },
                    '&:hover fieldset': {
                      borderColor: '#cbd5e1',
                      borderWidth: '1.5px',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#7C3AED',
                      borderWidth: '2px',
                    },
                  },
                  '& .MuiInputBase-input': {
                    textAlign: 'center',
                  },
                  '& .MuiInputLabel-root': {
                    transform: 'translate(14px, -9px) scale(0.75)',
                    backgroundColor: '#ffffff',
                    px: 0.5,
                    '&.Mui-focused': {
                      color: '#7C3AED',
                    },
                  },
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading || twoFactorToken.length !== 6}
                sx={{
                  py: 1.75,
                  fontSize: '1rem',
                  fontWeight: 600,
                  backgroundColor: '#7C3AED',
                  borderRadius: '8px',
                  boxShadow: '0 6px 20px rgba(124, 58, 237, 0.35)',
                  textTransform: 'none',
                  mb: 2,
                  '&:hover': {
                    backgroundColor: '#6D28D9',
                  },
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify Code'}
              </Button>

              <Button
                fullWidth
                variant="text"
                onClick={() => setTwoFactorRequired(false)}
                disabled={loading}
                sx={{
                  color: '#64748b',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.05)',
                  },
                }}
              >
                Back to Login
              </Button>
            </Box>
          ) : (
            <Box component="form" noValidate onSubmit={handleSubmit}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={formData.email}
              onChange={handleChange}
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ pr: 1.5 }}>
                    <Email sx={{ color: '#94a3b8', fontSize: '20px' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#ffffff',
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  height: '48px',
                  '& fieldset': {
                    borderColor: '#e2e8f0',
                    borderWidth: '1.5px',
                  },
                  '&:hover fieldset': {
                    borderColor: '#cbd5e1',
                    borderWidth: '1.5px',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#7C3AED',
                    borderWidth: '2px',
                  },
                },
                '& .MuiInputBase-input': {
                  py: 1.5,
                  '&::placeholder': {
                    opacity: 0.7,
                  },
                },
                '& .MuiInputLabel-root': {
                  transform: 'translate(14px, -9px) scale(0.75)',
                  backgroundColor: '#ffffff',
                  px: 0.5,
                  '&.Mui-focused': {
                    color: '#7C3AED',
                  },
                },
              }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleChange}
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ pr: 1.5 }}>
                    <Lock sx={{ color: '#94a3b8', fontSize: '20px' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end" sx={{ pr: 0.5 }}>
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={togglePasswordVisibility}
                      edge="end"
                      disabled={loading}
                      tabIndex={-1}
                      sx={{
                        color: '#94a3b8',
                        '&:hover': {
                          backgroundColor: 'transparent',
                        },
                      }}
                    >
                      {showPassword ? (
                        <VisibilityOff sx={{ fontSize: '20px' }} />
                      ) : (
                        <Visibility sx={{ fontSize: '20px' }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#ffffff',
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  height: '48px',
                  '& fieldset': {
                    borderColor: '#e2e8f0',
                    borderWidth: '1.5px',
                  },
                  '&:hover fieldset': {
                    borderColor: '#cbd5e1',
                    borderWidth: '1.5px',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#7C3AED',
                    borderWidth: '2px',
                  },
                },
                '& .MuiInputBase-input': {
                  py: 1.5,
                },
                '& .MuiInputLabel-root': {
                  transform: 'translate(14px, -9px) scale(0.75)',
                  backgroundColor: '#ffffff',
                  px: 0.5,
                  '&.Mui-focused': {
                    color: '#7C3AED',
                  },
                },
              }}
            />



            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                py: 1.75,
                fontSize: '1rem',
                fontWeight: 600,
                backgroundColor: '#7C3AED',
                borderRadius: '8px',
                boxShadow: '0 6px 20px rgba(124, 58, 237, 0.35)',
                textTransform: 'none',
                mb: 4,
                letterSpacing: '0.3px',
                '&:hover': {
                  backgroundColor: '#6D28D9',
                  boxShadow: '0 10px 28px rgba(124, 58, 237, 0.45)',
                  transform: 'translateY(-1px)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                },
                '&:disabled': {
                  backgroundColor: '#cbd5e1',
                  boxShadow: 'none',
                  color: '#94a3b8',
                },
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Log In'
              )}
            </Button>

            <Box sx={{ textAlign: 'center', pt: 2, borderTop: '1px solid #e2e8f0' }}>
              <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mb: 1.5, fontSize: '0.8rem' }}>
                Having trouble logging in?
              </Typography>
              <Link
                href="mailto:support@tsms.com"
                sx={{
                  fontWeight: 500,
                  textDecoration: 'none',
                  color: '#7C3AED',
                  fontSize: '0.9rem',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                Contact support@tsms.com
              </Link>
            </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Right Side: Branding - 35% width */}
      <Box
        sx={{
          flex: '0 0 35%',
          background: 'linear-gradient(135deg, #7C3AED 0%, #4C1D95 100%)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
          '@media (max-width: 960px)': {
            display: 'none',
          },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            opacity: 0.1,
            background: `radial-gradient(circle at 50% 50%, #ffffff 0%, transparent 50%), 
                        radial-gradient(circle at 100% 0%, #ffffff 0%, transparent 50%)`,
          }}
        />

        <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center', px: 3, maxWidth: '320px' }}>
          <Typography
            sx={{
              fontWeight: 800,
              color: 'white',
              letterSpacing: '-0.03em',
              mb: 2.5,
              fontSize: '2.25rem',
              lineHeight: 1.2,
            }}
          >
            TSMS Group
          </Typography>
          <Typography
            sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              fontWeight: 400,
              fontSize: '0.95rem',
              lineHeight: 1.7,
              letterSpacing: '0.2px',
            }}
          >
            Secure dashboard access to manage your digital signage campaigns with confidence.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
